import { useAppSelector } from '@hooks/app/redux';
import { eachHourOfInterval, endOfDay, getDay, isAfter, isSameDay, startOfDay } from 'date-fns';
import React from 'react';
import { ICalendarEvent } from '../../../../../../components/ui/calendar/calendars';
import { TExistingEVisitRule } from '../../../../../../types/e-visits';
import { IFormValues } from './add-form.schema';

/**
 * Generate disabled times for picker based on event params
 * @param param0 props
 * @returns returns disabled times
 */
export function generateDisabledTimes({
  minTime,
  maxTime,
  start,
}: {
  start?: Date;
  minTime?: Date;
  maxTime?: Date;
}) {
  if (!start || (!minTime && !maxTime)) {
    return {
      disabledHours: () => [],
      disabledMinutes: () => [],
    };
  }

  const min: Date = minTime ? new Date(minTime) : startOfDay(new Date(start));
  const max: Date = maxTime ? new Date(maxTime) : endOfDay(new Date(start));

  const enabledHours = eachHourOfInterval({
    start: min,
    end: max,
  }).map((d) => d.getHours());

  const minHour = min.getHours();
  const minMinute = min.getMinutes();
  const maxHour = max.getHours();
  const maxMinute = max.getMinutes();

  return {
    disabledHours: () => [...new Array(24).keys()].filter((k) => !enabledHours.includes(k)),
    disabledMinutes: (hour: number) => {
      if (hour > minHour && hour < maxHour) {
        return [];
      } else if (hour === minHour) {
        return [...new Array(minMinute).keys()];
      } else if (hour === maxHour) {
        const enabled = [...new Array(maxMinute + 1).keys()];
        return [...new Array(60).keys()].filter((k) => !enabled.includes(k));
      } else {
        return [...new Array(60).keys()];
      }
    },
  };
}

/**
 * Generate initial values from given event
 * @param event event data
 * @returns initial values for form
 */
export function generateInitialValues(event: ICalendarEvent<TExistingEVisitRule>): IFormValues {
  const start = startOfDay(new Date(event.start));

  const repeat: boolean = isRepeatingRule(event);
  const toDate = event.eventData?.toDate ? endOfDay(new Date(event.eventData?.toDate)) : undefined;

  return {
    id: event.eventData?.id,
    fromTime: event.minTime || event.eventData?.fromTime || new Date().toISOString(),
    toTime: event.maxTime || event.eventData?.toTime || new Date().toISOString(),
    description: event.eventData?.description || '',
    blockSize: event.eventData?.blockSize || 15,

    repeat,
    repeatTo: !!event.eventData?.toDate,

    days: event.eventData?.days || [getDay(new Date(event.start))],
    fromDate: start.toISOString(),
    toDate: toDate
      ? isAfter(toDate, start)
        ? toDate.toISOString()
        : start.toISOString()
      : undefined,

    expertiseCode: event.eventData?.expertiseCode || '',
    // @ts-expect-error
    medicalInstitutionId: event.eventData?.workspaceId || '',
    selectedTag: event.eventData?.tag
      ? {
          color: event.eventData.tag.color,
          label: event.eventData.tag.abbreviation,
          value: event.eventData.tag.id,
        }
      : null,
  };
}

export function isRepeatingEvent(fromDate?: string | Date, toDate?: string | Date) {
  let repeat: boolean = true;

  if (fromDate && toDate) {
    const from = new Date(fromDate);
    const to = new Date(toDate);

    if (isSameDay(from, to)) {
      repeat = false;
    }
  }

  return repeat;
}

export function isRepeatingRule(event: ICalendarEvent<TExistingEVisitRule>) {
  if (event.eventData?.id) {
    return isRepeatingEvent(event.eventData?.fromDate, event.eventData?.toDate);
  } else {
    return false;
  }
}

export function useInitialValues(event: ICalendarEvent<TExistingEVisitRule>) {
  const selectedInstitution = useAppSelector((state) => state.eVisits.selectedInstitution);

  return React.useMemo(
    () => ({
      ...generateInitialValues(event),
      medicalInstitutionId: selectedInstitution?.id ?? '',
    }),
    [event, selectedInstitution?.id],
  );
}
